import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { queryKeys } from '../lib/queryClient';
import * as areaService from '../services/areaService';
import type {
  CreateAreaRequest,
  UpdateAreaRequest,
  ListQueryParams
} from '../types/api';

// Areas list query
export const useAreas = (params?: ListQueryParams) => {
  return useQuery({
    queryKey: queryKeys.areas.list(params),
    queryFn: () => areaService.getAreas(params),
    select: (data) => {
      // API returns { areas: Area[], pagination: Pagination }
      // Return consistent structure with areas array
      return {
        areas: data.areas || [],
        pagination: data.pagination || null
      };
    },
  });
};

// Single area query
export const useArea = (id: number, view?: string) => {
  return useQuery({
    queryKey: queryKeys.areas.detail(id),
    queryFn: () => areaService.getArea(id.toString(), view),
    select: (data) => data,
    enabled: !!id,
  });
};

// Create area mutation
export const useCreateArea = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAreaRequest) => areaService.createArea(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.areas.lists() });
      notifications.show({
        title: 'Success',
        message: 'Area created successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to create area',
        color: 'red',
      });
    },
  });
};

// Update area mutation
export const useUpdateArea = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateAreaRequest }) =>
      areaService.updateArea(id.toString(), data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.areas.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.areas.detail(variables.id) });
      notifications.show({
        title: 'Success',
        message: 'Area updated successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update area',
        color: 'red',
      });
    },
  });
};

// Delete area mutation
export const useDeleteArea = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => areaService.deleteArea(id.toString()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.areas.lists() });
      notifications.show({
        title: 'Success',
        message: 'Area deleted successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to delete area',
        color: 'red',
      });
    },
  });
};

// School-specific areas query
export const useSchoolAreas = (schoolId: string, params?: ListQueryParams) => {
  return useQuery({
    queryKey: ['areas', 'school', schoolId, params],
    queryFn: () => areaService.getSchoolAreas(schoolId, params),
    enabled: !!schoolId,
    select: (data) => ({
      areas: data.areas || [],
      pagination: data.pagination || null
    }),
  });
};

// Area overview query for dashboard/monitoring
export const useAreaOverview = () => {
  return useQuery({
    queryKey: ['areas', 'overview'],
    queryFn: () => areaService.getAreaOverview(),
    refetchInterval: 60000, // Refresh every minute
    staleTime: 30000, // Consider stale after 30 seconds
  });
};

// Area cameras query
export const useAreaCameras = (areaId: string) => {
  return useQuery({
    queryKey: ['areas', areaId, 'cameras'],
    queryFn: () => areaService.getAreaCameras(areaId),
    enabled: !!areaId,
  });
};

// Assign camera to area mutation
export const useAssignCameraToArea = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ areaId, cameraId }: { areaId: string; cameraId: string }) =>
      areaService.assignCameraToArea(areaId, cameraId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['areas', variables.areaId, 'cameras'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.cameras.lists() });
      notifications.show({
        title: 'Success',
        message: 'Camera assigned to area successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to assign camera to area',
        color: 'red',
      });
    },
  });
};

// Remove camera from area mutation
export const useRemoveCameraFromArea = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ areaId, cameraId }: { areaId: string; cameraId: string }) =>
      areaService.removeCameraFromArea(areaId, cameraId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['areas', variables.areaId, 'cameras'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.cameras.lists() });
      notifications.show({
        title: 'Success',
        message: 'Camera removed from area successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to remove camera from area',
        color: 'red',
      });
    },
  });
};
